
import {
  ArrowLeftIcon,
  CaretDownIcon,
  ImagesIcon,
  InfoIcon,
  LockIcon,
  MapPinIcon,
  PaperPlaneTiltIcon,
  PawPrintIcon,
  UserIcon,
  WhatsappLogoIcon,
} from "@phosphor-icons/react";
import checkListado from "../../TALLER/checkListado.webp";
import { sanitizarEmail, sanitizarTexto } from "@encuentrayayuda/shared/lib/sanitizar";

const paises = [
  { codigo: "57", abreviatura: "CO", nombre: "Colombia" },
  { codigo: "58", abreviatura: "VE", nombre: "Venezuela" },
  { codigo: "593", abreviatura: "EC", nombre: "Ecuador" },
  { codigo: "51", abreviatura: "PE", nombre: "Perú" },
  { codigo: "52", abreviatura: "MX", nombre: "México" },
  { codigo: "1", abreviatura: "US", nombre: "Estados Unidos y Canadá" },
  { codigo: "34", abreviatura: "ES", nombre: "España" },
  { codigo: "54", abreviatura: "AR", nombre: "Argentina" },
  { codigo: "55", abreviatura: "BR", nombre: "Brasil" },
  { codigo: "56", abreviatura: "CL", nombre: "Chile" },
  { codigo: "507", abreviatura: "PA", nombre: "Panamá" },
];

const etiquetas = {
  PERRO: "Perro",
  GATO: "Gato",
  OTRO: "Otra mascota",
  MACHO: "Macho",
  HEMBRA: "Hembra",
  MUY_PEQUENO: "Muy pequeño",
  PEQUENO: "Pequeño",
  MEDIANO: "Mediano",
  GRANDE: "Grande",
  MUY_GRANDE: "Muy grande",
  NEGRO: "Negro",
  BLANCO: "Blanco",
  MARRON: "Marrón",
  CAFE_CLARO: "Café claro",
  BEIGE: "Beige",
  CREMA: "Crema",
  DORADO: "Dorado",
  AMARILLO: "Amarillo",
  ROJIZO: "Rojizo",
  GRIS: "Gris",
  CANELA: "Canela",
  ATIGRADO: "Atigrado",
  MANCHADO: "Manchado",
  BIEN: "Se ve bien",
  HERIDA: "Herida",
  ASUSTADA: "Asustada",
  HOMBRE: "Masculino",
  MUJER: "Femenino",
  NO_ESPECIFICA: "No especificado",
  DELGADA: "Delgada",
  MEDIANA: "Mediana",
  ROBUSTA: "Robusta",
};

function valorLegible(valor, respaldo = "Sin especificar") {
  return etiquetas[valor] ?? respaldo;
}

function fechaLegible(fecha) {
  if (!fecha) return "Fecha sin especificar";
  return new Intl.DateTimeFormat("es-CO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${fecha}T00:00:00Z`));
}

function FilaResumen({ Icon, titulo, children, onEdit }) {
  return (
    <article className="review-row">
      <Icon weight="duotone" aria-hidden="true" />
      <div><strong>{titulo}</strong>{children}</div>
      <button type="button" onClick={onEdit}>Editar</button>
    </article>
  );
}

function obtenerDatosEvento(datos, variante) {
  if (variante === "persona") {
    return { fecha: datos.fechaEvento, hora: datos.horaEvento, direccion: datos.direccionEvento };
  }
  if (variante === "perdida") {
    return { fecha: datos.fechaPerdida, hora: datos.horaPerdida, direccion: datos.direccionHallazgo };
  }
  return { fecha: datos.fechaHallazgo, hora: datos.horaHallazgo, direccion: datos.direccionHallazgo };
}

function ConfirmarMascotaForm({ datos, fotos, contacto, onContactChange, onBack, onEditStep, onPublicar, publicando, progreso, errorEnvio, variante = "encontrada", widgetAntiBot }) {
  const pais = paises.find(({ codigo }) => codigo === contacto.codigoPais) ?? paises[0];
  const esPerdida = variante === "perdida";
  const esPersona = variante === "persona";
  const { fecha, hora, direccion } = obtenerDatosEvento(datos, variante);

  function actualizar(campo, valor) {
    let valorSeguro = valor;
    if (campo === "nombre" || campo === "email") {
      valorSeguro = sanitizarTexto(valor, { recortar: false });
    }
    onContactChange((actual) => ({ ...actual, [campo]: valorSeguro }));
  }

  function actualizarTelefono(valor) {
    actualizar("telefono", valor.replace(/\D/g, "").slice(0, 10));
  }

  function enviar(evento) {
    evento.preventDefault();
    onContactChange((actual) => ({ ...actual, nombre: sanitizarTexto(actual.nombre), email: sanitizarEmail(actual.email) }));
    onPublicar();
  }

  return (
    <section className={`confirm-step confirm-step--${variante}`} aria-labelledby="confirm-title">
      <div className="confirm-step__intro">
        <div>
          <h2 id="confirm-title">Revisa tu información</h2>
          <p>Confirma que los datos sean correctos antes de publicar el reporte.</p>
        </div>
        <img src={checkListado} alt="Lista de información verificada" />
      </div>

      <form className="contact-form" onSubmit={enviar} noValidate>
        <div className="contact-form__heading">
          <h3>Datos de contacto <span>(obligatorios)</span></h3>
          <p>Los necesitamos si alguien tiene información sobre la {esPersona ? "persona" : "mascota"}.</p>
        </div>

        <aside className="contact-privacy"><InfoIcon weight="fill" /><span>Tu información de contacto no será pública. Solo será visible para el equipo encargado de gestionar el reporte.</span></aside>

        <label>{esPersona ? "Nombre completo del registrante" : "Nombre completo"} <b aria-hidden="true">*</b>
          <input type="text" autoComplete="name" maxLength="80" value={contacto.nombre} onChange={(e) => actualizar("nombre", e.target.value)} placeholder="Nombre y apellidos" required />
        </label>

        <label>WhatsApp de contacto <b aria-hidden="true">*</b>
          <span className="phone-field">
            <span className="country-select">
              <span aria-hidden="true">{pais.abreviatura}</span>
              <select value={contacto.codigoPais} onChange={(e) => actualizar("codigoPais", e.target.value)} aria-label="Código de país">
                {paises.map((opcion) => <option value={opcion.codigo} key={`${opcion.codigo}-${opcion.abreviatura}`}>{opcion.nombre} (+{opcion.codigo})</option>)}
              </select>
              <strong>+{contacto.codigoPais}</strong><CaretDownIcon weight="bold" />
            </span>
            <input type="tel" inputMode="numeric" pattern="[0-9]{10}" minLength="10" maxLength="10" autoComplete="tel-national" value={contacto.telefono} onChange={(e) => actualizarTelefono(e.target.value)} placeholder="300 123 4567" aria-describedby="phone-help" required />
          </span>
          <small id="phone-help"><WhatsappLogoIcon weight="fill" /> Ingresa los 10 dígitos del celular, sin espacios.</small>
        </label>

        <label>Correo electrónico <b aria-hidden="true">*</b>
          <input type="email" inputMode="email" autoComplete="email" maxLength="160" value={contacto.email} onChange={(e) => actualizar("email", e.target.value)} placeholder="nombre@correo.com" required />
          <small>Te enviaremos actualizaciones sobre tu reporte.</small>
        </label>

        <aside className="privacy-reason"><LockIcon weight="duotone" /><div><strong>¿Por qué pedimos estos datos?</strong><p>Son necesarios para contactarte rápidamente si alguien aporta información y para mantenerte informado.</p><a href="#politica-privacidad">Conoce nuestra política de privacidad</a></div></aside>

        <section className="review-section" aria-labelledby="review-title">
          <div className="review-section__heading"><h3 id="review-title">Resumen del reporte</h3><p>Revisa cada sección antes de publicar.</p></div>
          <div className="review-card">
            {esPersona ? (
              <FilaResumen Icon={UserIcon} titulo="Información y características" onEdit={() => onEditStep(1)}>
                <p><strong>{datos.nombrePersona}</strong></p>
                <p>{valorLegible(datos.sexo)}{datos.edadAproximada && ` · ${datos.edadAproximada} años`}{datos.estaturaAproximada && ` · ${datos.estaturaAproximada} m`}</p>
                {datos.contextura && <p>Contextura {valorLegible(datos.contextura).toLowerCase()}</p>}
                {datos.senalesParticulares && <p>{datos.senalesParticulares}</p>}
                {datos.vestimenta && <p>Vestimenta: {datos.vestimenta}</p>}
              </FilaResumen>
            ) : (
              <FilaResumen Icon={PawPrintIcon} titulo="Información de la mascota" onEdit={() => onEditStep(1)}>
                {esPerdida && <p><strong>{datos.nombreMascota}</strong></p>}
                <p>{valorLegible(datos.tipoMascota)} · {valorLegible(datos.sexo)} · {valorLegible(datos.tamano)}</p>
                <p>{valorLegible(datos.colorPrincipalPelaje, "Color sin especificar")}{!esPerdida && <> · {valorLegible(datos.estadoMascota)}</>}</p>
                {datos.caracteristicasParticulares && <p>{datos.caracteristicasParticulares}</p>}
              </FilaResumen>
            )}
            <FilaResumen Icon={MapPinIcon} titulo="Ubicación" onEdit={() => onEditStep(2)}>
              <p>{direccion || "Punto seleccionado en el mapa"}</p>
              {datos.puntoReferencia && <p>{datos.puntoReferencia}</p>}
              <p>{fechaLegible(fecha)} · {hora}</p>
            </FilaResumen>
            <FilaResumen Icon={ImagesIcon} titulo="Fotos" onEdit={() => onEditStep(3)}>
              <p>{fotos.length} foto{fotos.length === 1 ? "" : "s"} agregada{fotos.length === 1 ? "" : "s"}</p>
            </FilaResumen>
          </div>
        </section>

        <aside className="publish-note"><LockIcon weight="duotone" />Al publicar, el reporte será visible para personas que pueden ayudar. Tus datos de contacto seguirán protegidos.</aside>
        {/* Verificacion anti-bot. Va al final del formulario y no al principio
            para que el widget solo se cargue cuando el usuario esta por
            publicar: es una peticion a un tercero y la carga inicial manda. */}
        {widgetAntiBot && (
          <div className="antibot-field">
            {widgetAntiBot}
          </div>
        )}

        {/* Progreso real de la subida. En conexión precaria una barra que no se
            mueve es indistinguible de una app colgada. */}
        {publicando && (
          <div className="upload-progress" role="status" aria-live="polite">
            {progreso?.total ? (
              <>
                <strong>Subiendo foto {progreso.indice} de {progreso.total}</strong>
                <div className="upload-progress__bar">
                  <span style={{ width: `${progreso.porcentaje}%` }} />
                </div>
                <small>{progreso.porcentaje}%</small>
              </>
            ) : (
              <strong>Publicando tu reporte…</strong>
            )}
          </div>
        )}

        {errorEnvio && (
          <div className="form-message form-message--error" role="alert">
            <strong>{errorEnvio.message}</strong>
            {errorEnvio.detalles?.length > 0 && (
              <ul>
                {errorEnvio.detalles.map(({ campo, mensaje }, indice) => (
                  <li key={`${campo}-${indice}`}>
                    {campo ? <><b>{campo}:</b> {mensaje}</> : mensaje}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="confirm-step__actions">
          <button type="button" onClick={onBack} disabled={publicando}><ArrowLeftIcon weight="bold" /> Atrás</button>
          <button type="submit" disabled={publicando}>
            {publicando ? "Publicando…" : <>Publicar reporte <PaperPlaneTiltIcon weight="fill" /></>}
          </button>
        </div>
      </form>
    </section>
  );
}

export default ConfirmarMascotaForm;
